/**
 * ImageCarousel Component
 *
 * Responsive image carousel/slider for showcasing project photos
 *
 * Following CLAUDE.md:
 * - Uses Tailwind CSS utility classes for styling
 * - Brand-agnostic (no brand logic in components)
 * - Uses cn() utility for conditional classes
 * - Mobile-first responsive design
 * - Uses Shadcn Carousel component with Embla
 */

"use client";

import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";
import { OptimisedImage } from "@/components/ui/optimised-image";
import { cn } from "@/lib/utils";
import type { Image as ImageType } from "@/lib/validation/common";

export interface ImageCarouselProps {
	images: ImageType[];
	className?: string;
	aspectRatio?: "video" | "square" | "portrait";
}

export function ImageCarousel({
	images,
	className,
	aspectRatio = "video",
}: ImageCarouselProps) {
	if (!images || images.length === 0) return null;

	const aspectClasses = {
		video: "aspect-video",
		square: "aspect-square",
		portrait: "aspect-[3/4]",
	};

	return (
		<div className={cn("w-full", className)}>
			<Carousel
				className="w-full"
				opts={{
					align: "start",
					loop: true,
				}}
			>
				<CarouselContent>
					{images.map((image, index) => (
						<CarouselItem
							key={`${image.url}-${index}`}
							className="basis-full md:basis-1/2 lg:basis-1/3"
						>
							<div
								className={cn(
									"relative w-full overflow-hidden rounded-lg",
									aspectClasses[aspectRatio],
								)}
							>
								<OptimisedImage
									src={image.url}
									alt={image.alternativeText || `Image ${index + 1}`}
									focalX={image.focalX}
									focalY={image.focalY}
									fill
									className="object-cover"
									sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
								/>
							</div>
						</CarouselItem>
					))}
				</CarouselContent>
				<CarouselPrevious className="bg-background/90 hover:bg-background left-2 border-2 backdrop-blur-sm disabled:opacity-50" />
				<CarouselNext className="bg-background/90 hover:bg-background right-2 border-2 backdrop-blur-sm disabled:opacity-50" />
			</Carousel>
		</div>
	);
}
